import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDownSharp';
import { useIntl } from 'react-intl';
import DatePicker from '../DatePicker';
import DatePickerV5 from '../DatePickerV5';
import CalendarDateInput from './CalendarDateInput';

const useStyles = makeStyles(theme => ({
  adornment: {
    marginLeft: 0
  }
}));

const getAriaLabel = ariaLabel => ({ 'aria-label': ariaLabel });

export const CalendarDate = ({
  value,
  onChange, // Remove from rest
  label,
  fullWidth = true,
  className,
  keyboard,
  format = 'D',
  useV5DatePicker = false,
  v5DatePickerClasses,
  ...rest
}) => {
  const classes = useStyles();

  const defaultPickerProps = {
    className,
    autoOk: true,
    label,
    clearable: true,
    value,
    fullWidth,
    onChange
  };

  const { formatMessage } = useIntl();

  const InputAdornmentProps = useMemo(
    () => ({
      className: classes.adornment
    }),
    []
  );

  const dialogProps = useMemo(
    () => ({
      TransitionProps: {
        role: 'presentation'
      },
      'aria-hidden': true,
      PaperProps: {
        ...getAriaLabel(formatMessage({ id: 'calendar.datePicker' }))
      }
    }),
    [formatMessage]
  );

  if (useV5DatePicker) {
    return (
      <DatePickerV5
        {...defaultPickerProps}
        format={format}
        DialogProps={dialogProps}
        leftArrowButtonProps={getAriaLabel(
          formatMessage({ id: 'dateButton.previous' })
        )}
        rightArrowButtonProps={getAriaLabel(
          formatMessage({ id: 'dateButton.next' })
        )}
        keyboard={keyboard}
        classes={v5DatePickerClasses}
        {...rest}
      />
    );
  }

  if (keyboard) {
    return (
      <KeyboardDatePicker
        {...defaultPickerProps}
        TextFieldComponent={CalendarDateInput}
        InputAdornmentProps={InputAdornmentProps}
        keyboardIcon={<ArrowDropDownIcon />}
        DialogProps={dialogProps}
        leftArrowButtonProps={getAriaLabel(
          formatMessage({ id: 'dateButton.previous' })
        )}
        rightArrowButtonProps={getAriaLabel(
          formatMessage({ id: 'dateButton.next' })
        )}
        format={format}
        dateFormat={format}
        onValueChange={onChange}
        {...rest}
      />
    );
  }

  return (
    <DatePicker
      DialogProps={dialogProps}
      leftArrowButtonProps={getAriaLabel(
        formatMessage({ id: 'dateButton.previous' })
      )}
      rightArrowButtonProps={getAriaLabel(
        formatMessage({ id: 'dateButton.next' })
      )}
      {...defaultPickerProps}
      format={format}
      {...rest}
    />
  );
};

CalendarDate.propTypes = {
  className: PropTypes.string,
  value: PropTypes.shape({
    day: PropTypes.number,
    month: PropTypes.number,
    year: PropTypes.number
  }),
  onChange: PropTypes.func,
  onValueChange: PropTypes.func,
  label: PropTypes.node,
  fullWidth: PropTypes.bool,
  keyboard: PropTypes.bool,
  format: PropTypes.string,
  useV5DatePicker: PropTypes.bool,
  v5DatePickerClasses: PropTypes.object
};

export default CalendarDate;
